import { useState, useEffect, useCallback } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useQuery, useInfiniteQuery } from '@tanstack/react-query';
import { useLocalization } from '../contexts/LocalizationContext';
import type { Coupon } from '../types';
import { fetchMemberCoupons, fetchMemberCouponsExpired, fetchMemberCouponsUsed } from '../api/auth';
import { routes } from '../config';

type CouponFilter = 'active' | 'used' | 'expired';

export default function CouponList() {
    const { t } = useLocalization();
    const navigate = useNavigate();
    const [couponFilter, setCouponFilter] = useState<CouponFilter>('active');

    // Query for active coupons (no pagination)
    const activeQuery = useQuery({
        queryKey: ['coupons', 'active'],
        queryFn: async () => {
            const data = await fetchMemberCoupons();
            return Array.isArray(data) ? data : [];
        },
        staleTime: 0,
        refetchOnMount: 'always',
    });

    // Infinite query for expired coupons
    const expiredQuery = useInfiniteQuery({
        queryKey: ['coupons', 'expired'],
        queryFn: async ({ pageParam = 0 }) => {
            const data = await fetchMemberCouponsExpired(pageParam, 20);
            return {
                content: Array.isArray(data.content) ? data.content : [],
                totalPages: data.totalPages || 0,
                currentPage: pageParam,
            };
        },
        getNextPageParam: (lastPage) => {
            const nextPage = lastPage.currentPage + 1;
            return nextPage < lastPage.totalPages ? nextPage : undefined;
        },
        initialPageParam: 0,
        staleTime: 0,
        refetchOnMount: 'always',
    });

    // Infinite query for used coupons
    const usedQuery = useInfiniteQuery({
        queryKey: ['coupons', 'used'],
        queryFn: async ({ pageParam = 0 }) => {
            const data = await fetchMemberCouponsUsed(pageParam, 20);
            return {
                content: Array.isArray(data.content) ? data.content : [],
                totalPages: data.totalPages || 0,
                currentPage: pageParam,
            };
        },
        getNextPageParam: (lastPage) => {
            const nextPage = lastPage.currentPage + 1;
            return nextPage < lastPage.totalPages ? nextPage : undefined;
        },
        initialPageParam: 0,
        staleTime: 0,
        refetchOnMount: 'always',
    });

    // Get current query based on filter
    const getCurrentQuery = () => {
        switch (couponFilter) {
            case 'active':
                return activeQuery;
            case 'expired':
                return expiredQuery;
            case 'used':
                return usedQuery;
        }
    };

    const currentQuery = getCurrentQuery();

    // Extract coupons from the current query with deduplication
    const coupons = couponFilter === 'active'
        ? (activeQuery.data || [])
        : (couponFilter === 'expired'
            ? (() => {
                const allCoupons = expiredQuery.data?.pages.flatMap(page => page.content) || [];
                // Deduplicate by couponId
                const uniqueCoupons = Array.from(
                    new Map(allCoupons.map(coupon => [coupon.couponId, coupon])).values()
                );
                return uniqueCoupons;
            })()
            : (() => {
                const allCoupons = usedQuery.data?.pages.flatMap(page => page.content) || [];
                // Deduplicate by couponId
                const uniqueCoupons = Array.from(
                    new Map(allCoupons.map(coupon => [coupon.couponId, coupon])).values()
                );
                return uniqueCoupons;
            })());

    const loadingCoupons = currentQuery.isLoading || currentQuery.isFetching;
    const loadingMore = couponFilter !== 'active' && (currentQuery as any).isFetchingNextPage;
    const hasMore = couponFilter !== 'active' && (currentQuery as any).hasNextPage;

    // Infinite scroll handler - using window scroll
    const handleScroll = useCallback(() => {
        if (couponFilter === 'active') return;
        if (loadingMore || !hasMore) return;

        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        const scrollHeight = document.documentElement.scrollHeight;
        const clientHeight = window.innerHeight;

        // Load more when scrolled to 80% of the content
        if (scrollTop + clientHeight >= scrollHeight * 0.8) {
            if (couponFilter === 'expired') {
                expiredQuery.fetchNextPage();
            } else if (couponFilter === 'used') {
                usedQuery.fetchNextPage();
            }
        }
    }, [loadingMore, hasMore, couponFilter, expiredQuery, usedQuery]);

    // Attach scroll listener to window
    useEffect(() => {
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, [handleScroll]);



    // Get coupon discount value for display
    const getCouponValue = (coupon: Coupon) => {
        if (coupon.discountType === 'amount' && coupon.discountAmountCents) {
            return `$${(coupon.discountAmountCents / 100).toFixed(2)}`;
        } else if (coupon.discountType === 'percent' && coupon.discountPercent) {
            return `${coupon.discountPercent}%`;
        }
        return '$0';
    };

    // Format date helper
    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleDateString();
    };

    const handleCouponClick = (_: Coupon, index: number) => {
        navigate(routes.couponDetail, {
            state: {
                coupons: coupons,
                selectedIndex: index
            }
        });
    };

    // Ticket colour palette (matches HTML demo)
    const ticketBg = '#FFF1EB';
    const ticketOffer = '#E85A22';
    const ticketDivider = 'rgba(232, 90, 34, 0.28)';

    return (
        <Box>
            {/* Filter chips */}
            <Box sx={{ display: 'flex', gap: 1, mb: 2, ml: 0 }}>
                {(['active', 'used', 'expired'] as CouponFilter[]).map((f) => (
                    <Box
                        key={f}
                        component="button"
                        type="button"
                        onClick={() => setCouponFilter(f)}
                        sx={{
                            padding: '7px 16px',
                            borderRadius: '99px',
                            fontSize: '13px',
                            fontWeight: 600,
                            border: couponFilter === f ? '1px solid #7BA88A' : '1px solid #D0D0D0',
                            background: couponFilter === f ? '#7BA88A' : '#fff',
                            color: couponFilter === f ? '#fff' : '#666',
                            cursor: 'pointer',
                            fontFamily: 'inherit',
                            transition: 'background 0.12s ease, color 0.12s ease, border-color 0.12s ease',
                            '&:active:not([data-active])': { background: '#F5F5F5' },
                        }}
                    >
                        {f === 'active' ? t("home.coupons.tabs.active") : f === 'used' ? t("home.coupons.tabs.used") : t("home.coupons.tabs.expired")}
                    </Box>
                ))}
            </Box>

            {loadingCoupons ? (
                <Typography sx={{ textAlign: 'center', py: 4, color: '#999', minHeight: '220px' }}>
                    {t("common.loading")}
                </Typography>
            ) : coupons.length === 0 ? (
                <Typography sx={{ textAlign: 'center', py: 4, color: '#999' }}>
                    {t("home.coupons.empty")}
                </Typography>
            ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {coupons.map((coupon) => (
                        <Box
                            key={coupon.couponId}
                            onClick={() => handleCouponClick(coupon, coupons.indexOf(coupon))}
                            sx={{
                                position: 'relative',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'stretch',
                                backgroundColor: ticketBg,
                                borderRadius: '12px',
                                height: '88px',
                                overflow: 'hidden',
                                boxShadow: '0 1px 4px rgba(232,90,34,0.06)',
                                transition: 'transform 0.12s ease',
                                '&:active': { transform: 'scale(0.985)' },
                            }}
                        >
                            {/* Quantity badge */}
                            {couponFilter === 'active' && coupon.memberRedemptionCount != null && coupon.memberRedemptionCount > 1 && (
                                <Box sx={{
                                    position: 'absolute', top: '6px', right: '8px', zIndex: 3,
                                    background: '#E85A22', color: '#fff',
                                    fontSize: '10px', fontWeight: 700, lineHeight: 1,
                                    px: '6px', py: '3px', borderRadius: '99px',
                                }}>
                                    x{coupon.memberRedemptionCount}
                                </Box>
                            )}
                            {/* Top notch (half-circle cut into ticket) */}
                            <Box sx={{
                                position: 'absolute',
                                left: '65px',
                                top: '-7px',
                                width: '14px',
                                height: '14px',
                                borderRadius: '50%',
                                backgroundColor: 'white',
                                zIndex: 2,
                            }} />
                            {/* Bottom notch */}
                            <Box sx={{
                                position: 'absolute',
                                left: '65px',
                                bottom: '-7px',
                                width: '14px',
                                height: '14px',
                                borderRadius: '50%',
                                backgroundColor: 'white',
                                zIndex: 2,
                            }} />

                            {/* Left icon section */}
                            <Box sx={{
                                width: '72px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                                position: 'relative',
                            }}>
                                {/* Dashed vertical divider */}
                                <Box sx={{
                                    position: 'absolute',
                                    right: 0,
                                    top: '8px',
                                    bottom: '8px',
                                    borderRight: `1.5px dashed ${ticketDivider}`,
                                }} />
                                {/* Coupon icon */}
                                <svg width="32" height="32" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M8 6v7.5c0 1.2-.7 2-1.5 2.5V22" stroke="#E85A22" strokeWidth="1.6" strokeLinecap="round"/>
                                    <path d="M6.5 6v6.5M9.5 6v6.5" stroke="#E85A22" strokeWidth="1.4" strokeLinecap="round"/>
                                    <path d="M19.5 6v16" stroke="#E85A22" strokeWidth="1.6" strokeLinecap="round"/>
                                    <path d="M19.5 6c2.8.2 4.5 2.2 4.5 5v1.5c0 2.2-1.4 3.5-4.5 3.5" stroke="#E85A22" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                            </Box>

                            {/* Right content section */}
                            <Box sx={{
                                flex: 1,
                                padding: '14px 14px 14px 16px',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'center',
                                position: 'relative',
                                overflow: 'hidden',
                                // Watermark % sign
                                '&::after': {
                                    content: '"%"',
                                    position: 'absolute',
                                    right: '10px',
                                    bottom: '-8px',
                                    fontSize: '56px',
                                    fontWeight: 800,
                                    color: 'rgba(232, 90, 34, 0.06)',
                                    lineHeight: 1,
                                    pointerEvents: 'none',
                                },
                            }}>
                                <Typography sx={{
                                    fontSize: '12px',
                                    color: '#666',
                                    fontWeight: 500,
                                    lineHeight: 1.2,
                                }}>
                                    {coupon.name}
                                </Typography>
                                <Typography sx={{
                                    fontSize: '20px',
                                    fontWeight: 700,
                                    color: ticketOffer,
                                    lineHeight: 1.2,
                                    letterSpacing: '-0.2px',
                                    mt: '2px',
                                }}>
                                    {getCouponValue(coupon)}
                                </Typography>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: '4px' }}>
                                    <Typography sx={{
                                        fontSize: '11px',
                                        color: couponFilter === 'expired' || couponFilter === 'used'
                                            ? '#E8A030'
                                            : '#8A8A8A',
                                        fontWeight: 500,
                                        lineHeight: 1.2,
                                    }}>
                                        {couponFilter === 'used'
                                            ? t("home.coupons.used")
                                            : couponFilter === 'expired'
                                                ? t("home.coupons.expiredOn", { date: coupon.expiresAt ? formatDate(coupon.expiresAt) : formatDate(coupon.startsAt) })
                                                : coupon.expiresAt
                                                    ? t("home.coupons.validPeriod", { startDate: formatDate(coupon.startsAt), endDate: formatDate(coupon.expiresAt) })
                                                    : t("home.coupons.noExpiry")
                                        }
                                    </Typography>
                                    {couponFilter === 'active' && (
                                        <Typography
                                            component="span"
                                            sx={{ fontSize: '12px', fontWeight: 600, color: '#7BA88A', lineHeight: 1 }}
                                            onClick={(e) => { e.stopPropagation(); handleCouponClick(coupon, coupons.indexOf(coupon)); }}
                                        >
                                            {t("home.coupons.use")}
                                        </Typography>
                                    )}
                                </Box>
                            </Box>
                        </Box>
                    ))}

                    {/* Loading more indicator */}
                    {loadingMore && (
                        <Box sx={{ textAlign: 'center', py: 2 }}>
                            <CircularProgress size={24} sx={{ color: '#7BA88A' }} />
                        </Box>
                    )}
                </Box>
            )}
        </Box>
    );
}